import ResourceModule, { type ResourceModuleConfig } from "@/pages/admin/components/ResourceModule";
import { StatusPill, type Column } from "@/pages/admin/components/AdminUI";
import { optionsFrom } from "@/lib/adminContent";
import { ADMIN_USER_STATUS, metaFor, type AdminUser } from "@/lib/adminSystem";
import { formatDate } from "@/pages/admin/format";

const ROLE_OPTIONS = [
  "Super Admin",
  "Operations Manager",
  "Farm Coordinator",
  "Finance",
  "Content Editor",
].map((value) => ({ value, label: value }));

const columns: Column<AdminUser>[] = [
  {
    key: "name",
    header: "Admin",
    primary: true,
    render: (row) => (
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-semibold shrink-0">
          {row.name.slice(0, 2).toUpperCase()}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground-950 whitespace-nowrap">{row.name}</p>
          <p className="text-xs text-foreground-500 mt-0.5">{row.email}</p>
        </div>
      </div>
    ),
  },
  { key: "role", header: "Role", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.role}</span> },
  { key: "phone", header: "Phone", render: (row) => <span className="text-sm text-foreground-700 whitespace-nowrap">{row.phone || "—"}</span> },
  { key: "lastLogin", header: "Last login", render: (row) => <span className="text-sm text-foreground-600 whitespace-nowrap">{formatDate(row.last_login)}</span> },
  { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(ADMIN_USER_STATUS, row.status)} /> },
];

const config: ResourceModuleConfig<AdminUser> = {
  table: "admin_users",
  orderBy: "name",
  eyebrow: "Administration",
  title: "Admin Users",
  subtitle: "Everyone with access to the admin portal, the role they hold and when they last signed in.",
  searchPlaceholder: "Search by name, email, role…",
  searchKeys: ["name", "email", "role", "phone"],
  filters: [
    { key: "role", label: "Role", options: ROLE_OPTIONS },
    { key: "status", label: "Status", options: optionsFrom(ADMIN_USER_STATUS) },
  ],
  statusMap: ADMIN_USER_STATUS,
  statusKey: "status",
  stats: (rows) => [
    { label: "Admin users", value: rows.length, icon: "ri-admin-line", tone: "primary" },
    { label: "Active", value: rows.filter((row) => row.status === "active").length, icon: "ri-user-follow-line", tone: "primary" },
    { label: "Roles in use", value: new Set(rows.map((row) => row.role)).size, icon: "ri-shield-user-line", tone: "secondary" },
    { label: "Suspended", value: rows.filter((row) => row.status === "suspended").length, icon: "ri-forbid-line", tone: "accent" },
  ],
  columns,
  fields: [
    { key: "name", label: "Full name" },
    { key: "email", label: "Email" },
    { key: "role", label: "Role", type: "select", options: ROLE_OPTIONS },
    { key: "phone", label: "Phone" },
    { key: "status", label: "Status", type: "select", options: optionsFrom(ADMIN_USER_STATUS) },
  ],
  createFields: [
    { key: "name", label: "Full name" },
    { key: "email", label: "Email" },
    { key: "role", label: "Role", type: "select", options: ROLE_OPTIONS },
    { key: "phone", label: "Phone" },
  ],
  newDefaults: { name: "", email: "", role: "Operations Manager", phone: "", status: "active" },
  emptyTitle: "No admin users yet",
  emptyMessage: "Invite team members and assign them a role to give them access to the portal.",
  rowTitle: (row) => row.name,
};

export default function AdminUsers() {
  return <ResourceModule config={config} />;
}